import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { GoogleMap } from "@react-google-maps/api";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminMainText,
  AdminMetadataText,
  AdminAlertText,
} from "../../sections/AdminDashboard/shared/adminUx";
import { AdminWidgetHeader, AdminWidgetPeriodFilter } from "../BusinessDashboard/AdminWidgetChrome";
import { useGoogleMaps } from "../../context/GoogleMapsProvider";
import profileImage from "../../assets/img/P1.jpeg";

const ReviewsMapWidget = () => {
  const [timeFilterPeriod, setTimeFilterPeriod] = useState("7 Days");
  const [hoveredPin, setHoveredPin] = useState(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useGoogleMaps();

  // Map center (North Melbourne) - memoized
  const center = useMemo(() => ({ lat: -37.8063, lng: 144.9518 }), []);

  // Map container style - memoized
  const mapContainerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Custom map styles - memoized
  const mapStyles = useMemo(() => ([
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [{ visibility: 'simplified' }]
    },
    {
      featureType: 'landscape',
      stylers: [{ color: '#f8f9fa' }]
    },
    {
      featureType: 'water',
      stylers: [{ color: '#e3f2fd' }]
    }
  ]), []);

  // Options - memoized
  const mapOptions = useMemo(() => ({
    styles: mapStyles,
    mapTypeId: 'roadmap',
    disableDefaultUI: true
  }), [mapStyles]);

  // Set first pin as hovered by default
  useEffect(() => {
    if (mapData.locationPins.length > 0) {
      setHoveredPin(mapData.locationPins[3]);
    }
  }, []);

  // Review data from BusinessReviews component
  const allReviews = [
    {
      id: 1,
      name: "Jozeph J",
      location: "Duffy, Australian Capital Territory, 2611",
      rating: 5,
      text: "Donec consequat, lacus vel ornare laoreet, nibh lacus blandit est, venenatis feugiat augue augue eu ipsum. a b c Vestibulum pulvinar eleifend facilisis. Aliquam a tempor orci, vitae ullamcorper mauris.",
      timeAgo: "1 hour ago",
      tags: ["Plumbing", "Emergency", "24/7 Service"]
    },
    {
      id: 2,
      name: "Sarah M",
      location: "Duffy, Australian Capital Territory, 2611",
      rating: 4,
      text: "Vestibulum pulvinar eleifend facilisis. Aliquam a tempor orci, vitae ullamcorper mauris. Cras sollicitudin turpis quis tempus pretium. Maecenas vehicula mauris sagittis justo blandit.",
      timeAgo: "2 hours ago",
      tags: ["Electrical", "Installation", "Safety Check"]
    },
    {
      id: 3,
      name: "Michael R",
      location: "Duffy, Australian Capital Territory, 2611",
      rating: 3,
      text: "Cras sollicitudin turpis quis tempus pretium. Maecenas vehicula mauris sagittis justo blandit, sed consequat lacus dictum. Sed molestie diam neque, ut consectetur dui iaculis vitae.",
      timeAgo: "3 hours ago",
      tags: ["Carpentry", "Repair", "Custom Work"]
    },
    {
      id: 4,
      name: "Emma L",
      location: "Canberra, Australian Capital Territory, 2600",
      rating: 5,
      text: "Exceptional service! The team went above and beyond my expectations. Professional, punctual, and the quality of work was outstanding. Highly recommend to anyone looking for reliable service.",
      timeAgo: "5 hours ago",
      tags: ["Painting", "Interior", "Premium Finish"]
    },
    {
      id: 5,
      name: "David K",
      location: "Belconnen, Australian Capital Territory, 2617",
      rating: 4,
      text: "Very good experience overall. The work was completed on time and the communication was clear throughout the process. Would definitely use their services again.",
      timeAgo: "1 day ago",
      tags: ["Landscaping", "Design", "Maintenance"]
    }
  ];

  // Map data with location pins using review data
  const mapData = {
    totalPins: allReviews.length,
    locationPins: allReviews.map((review, index) => ({
      id: review.id,
      x: [20, 60, 80, 40, 70][index] || 50, // Spread pins across map
      y: [30, 45, 25, 70, 60][index] || 50,
      location: review.location.split(',')[0], // Extract city name
      businessName: ["Pro Plumbing", "Elite Painters", "Master Carpentry", "Green Landscapes", "Sparkle Clean"][index] || "Business",
      rating: review.rating,
      timeAgo: review.timeAgo,
      text: review.text,
      businessLogo: require("../../assets/img/User.png"),
      reviewerName: review.name,
      tags: review.tags
    }))
  };

  return (
    <DashboardMainContainer className="masonry-item w-[500px] h-[400px] flex flex-col overflow-hidden">
      <AdminWidgetHeader title="Reviews Coverage Map">
        <AdminWidgetPeriodFilter
          period={timeFilterPeriod}
          onPeriodChange={setTimeFilterPeriod}
        />
      </AdminWidgetHeader>
      <div className="flex-1">
        {/* Map with location pins */}
        <div className="relative rounded-lg h-full">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={9}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={mapOptions}
            >
              {/* Empty - we'll use custom pins overlaid on top */}
            </GoogleMap>
          ) : loadError ? (
            <div className="w-full h-full min-h-[300px] bg-mistygray border border-lightgray flex items-center justify-center">
              <div className="text-center p-4">
                <AdminAlertText className="text-alertred mb-2">Google Maps Error</AdminAlertText>
                <AdminMainText className="text-mediumgray block">
                  Failed to load Google Maps API
                </AdminMainText>
                <AdminMetadataText className="text-mediumgray mt-2 block">
                  Check your API key configuration
                </AdminMetadataText>
              </div>
            </div>
          ) : (
            <div className="w-full h-full min-h-[300px] bg-mistygray border border-lightgray flex items-center justify-center">
              <AdminMainText className="text-mediumgray">Loading Google Maps...</AdminMainText>
            </div>
          )}
          
          {/* Location Pins - Overlaid on top of the map */}
          {mapData.locationPins.map((pin) => (
            <div
              key={pin.id}
              className="absolute cursor-pointer group"
              style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
              onMouseEnter={() => setHoveredPin(pin)}
              onMouseLeave={() => setHoveredPin(null)}
            >
              {/* Pin Icon */}
              <div className="relative">
                <svg className="w-6 h-6 text-primaryblue drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                
                {/* Hover Popup - Shows business and review */}
                {hoveredPin && hoveredPin.id === pin.id && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white rounded-lg shadow-lg p-3 min-w-[220px] max-w-[220px] z-20">
                    {/* Business Info - Main Display */}
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-lightgray">
                          <img
                            src={pin.businessLogo}
                            alt={pin.businessName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <AdminMainText className="text-offblack text-sm block">
                            {pin.businessName}
                          </AdminMainText>
                          <div className="flex items-center gap-1">
                            <div className="flex items-center">
                              {[...Array(pin.rating)].map((_, i) => (
                                <span key={i} className="text-yellow-400 text-xs">
                                  ★
                                </span>
                              ))}
                            </div>
                            <AdminMetadataText className="text-mediumgray">
                              • {pin.timeAgo}
                            </AdminMetadataText>
                          </div>
                        </div>
                      </div>
                      <AdminMainText className="text-mediumgray text-xs leading-relaxed line-clamp-2 w-full block">
                        &ldquo;{pin.text}&rdquo;
                      </AdminMainText>
                    </div>
                    
                    {/* Arrow pointing down */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                      <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardMainContainer>
  );
};

export default ReviewsMapWidget;

